import type { LocationQuery } from "vue-router";
import { rollbar } from "../../rollbar";

/** Formal German (Sie), informal German (Du), English, Ukrainian */
export const APP_LOCALES = ["de", "de-informal", "en", "uk"] as const;
export type AppLocale = (typeof APP_LOCALES)[number];

const SESSION_APP_LOCALE_KEY = "covomo.appLocale";

function isValidAppLocale(s: string): s is AppLocale {
  return (APP_LOCALES as readonly string[]).includes(s);
}

/** Last explicit UI language for this tab (set when `lang` appears in the URL). */
export function readStoredAppLocale(): AppLocale | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SESSION_APP_LOCALE_KEY);
    if (!raw || !isValidAppLocale(raw)) return null;
    return raw;
  } catch (e) {
    rollbar?.error(e as Error, { context: "appLocale:readStoredAppLocale" });
    return null;
  }
}

export function persistAppLocale(loc: AppLocale): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(SESSION_APP_LOCALE_KEY, loc);
  } catch (e) {
    /* ignore quota / private mode */
    rollbar?.error(e as Error, { context: "appLocale:persistAppLocale" });
  }
}

function normalizeLangToken(raw: string): string {
  return raw.trim().toLowerCase();
}

/** `lang=de` (Sie) · `lang=de-informal` (Du) · `lang=en` · `lang=uk` */
export function localeFromRouteQuery(q: LocationQuery): AppLocale | null {
  const langRaw = q.lang;
  const langStr =
    langRaw === undefined || langRaw === null
      ? ""
      : Array.isArray(langRaw)
        ? String(langRaw[langRaw.length - 1])
        : String(langRaw);
  const lang = langStr ? normalizeLangToken(langStr) : "";

  if (lang === "en") return "en";
  if (lang === "de-informal") return "de-informal";
  if (lang === "de") return "de";
  if (lang === "uk") return "uk";
  return null;
}

export function htmlLangForAppLocale(loc: AppLocale): string {
  if (loc === "de" || loc === "de-informal") return "de";
  if (loc === "uk") return "uk";
  return loc;
}
